import { Fragment, type ComponentChildren } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Epigraph, Footer, goStates, Masthead, Nav, NON_STATES, OutcomeKey, PhoneMast, Pinned, StatesPanel } from './components/Chrome';
import { ItemModal } from './components/ItemModal';
import { MemberCard } from './components/MemberCard';
import { bandList, displayOrder, ItemPage, jumpToBand, jumpToMonthOf, MemberPage, memberMonths } from './components/PhonePages';
import { FILTERS as PHONE_FILTERS, FindSheet, JumpSheet, MenuSheet, MonthsSheet, OptionsSheet, primeKeyboard, Sheet, termMonths, useLayer, type Filter } from './components/PhoneSheets';
import { ReportPage } from './components/ReportPage';
import { pageTitle } from './components/Share';
import { NotFound, SupportPage } from './components/SupportPage';
import { TRUST_TITLES, TrustDoc, type TrustPage } from './components/TrustPages';
import { loadDistrictMap, loadGrid, loadMeta, peopleIn, type Band, type Grid as GridDoc, type Meta, type Row, type Term } from './data';
import { fmtLong, ordinal } from './format';
import { accuracyWords, forget, fromPosition, keep, loadDistrict, lookUp, type Choice, type District } from './district';
import { Grid, outcomeDay, type PinnedBand, type SortBy } from './grid/Grid';
import { Bar, PAGE, PhoneBar, PhoneGrid } from './grid/PhoneGrid';
import { usePhone } from './phone';
import { itemText, personText, useElsewhere } from './search';
import { backLabel, closeOverlay, closeSheet, fromReps, leaveSheet, navigate, navigateUnderSheet, openSheet, parse, stepTo, usePath, useSheet } from './router';
import ROUTES from './routes.json';

/** The address of a term's grid: the current one is the site root. */
export const termPath = (t: Term) => (t.current ? '/' : '/' + t.label.replace(/–/g, '-'));

export function App() {
  const path = usePath();
  const route = parse(path);
  const phone = usePhone();
  const sheet = useSheet();
  // The four trust pages, Support Us and the not-found page share the chrome and the footer,
  // and have no grid. The design's state address opens Support Us (no State pages in v1).
  const trust: TrustPage | null = route.page in TRUST_TITLES ? (route.page as TrustPage) : null;
  const support = route.page === 'support' || route.page === 'state';
  const [meta, setMeta] = useState<Meta | null>(null);
  // A term address the record does not hold ("/1999-2000") is not the current term under
  // another name: it is an address the site does not have, as the server also answers (404).
  const unknownTerm = route.page === 'home' && !!route.term && !!meta
    && !meta.terms.some((t) => t.label.replace(/–/g, '-') === route.term);
  const missing = route.page === 'missing' || unknownTerm;
  const report = route.page === 'report';
  const page = !!trust || support || missing || report;
  useEffect(() => { if (route.page === 'state') navigate('/support#states', { replace: true }); }, [route.page]);
  const [grid, setGrid] = useState<GridDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  // People by time in office (the default) or by name (Dan, 16 Oct 2026), for this visit only: Privacy promises
  // the site keeps nothing but the chosen district.
  const [sortBy, setSortBy] = useState<SortBy>('tenure');
  const [q, setQ] = useState('');
  const [openLeft, setOpenLeft] = useState<Record<Band, boolean>>({ executive: false, senate: false, house: false });

  useEffect(() => { loadMeta().then(setMeta).catch((e) => setError(String(e))); }, []);

  // Which term: the path names one by its years ("/2023-2024"); an item's id starts with
  // its Congress; otherwise the default (the current Congress).
  const termLabel = route.page === 'home' ? route.term : null;
  const itemCongress = route.page === 'home' && route.item ? +route.item.split('-')[0] : null;
  const memberId = route.page === 'home' ? route.member : null;
  // A member's address names no term, so the card opens over the term already on screen.
  const lastTerm = useRef<number | null>(null);
  const term = useMemo(() => {
    if (!meta) return null;
    const byLabel = termLabel && meta.terms.find((t) => t.label.replace(/–/g, '-') === termLabel);
    const byItem = itemCongress && meta.terms.find((t) => t.congress === itemCongress);
    const byMember = memberId && lastTerm.current && meta.terms.find((t) => t.congress === lastTerm.current);
    return byLabel || byItem || byMember || meta.terms.find((t) => t.congress === meta.default) || meta.terms[0];
  }, [meta, termLabel, itemCongress, memberId]);
  useEffect(() => { if (term) lastTerm.current = term.congress; }, [term?.congress]);
  const close = useCallback(() => { if (term) closeOverlay(termPath(term)); }, [term]);

  // No grid is fetched for a page without one. A grid already loaded is kept, so returning to the
  // record does not flash the loading state; `current` is only ever the term on screen.
  useEffect(() => {
    if (!term || page) return;
    let live = true;
    loadGrid(term.grid).then((g) => { if (live) setGrid(g); }).catch((e) => setError(String(e)));
    return () => { live = false; };
  }, [term?.grid, page]);
  const current = grid && term && grid.congress === term.congress ? grid : null;

  // The tab's title, as the server titles the same address. An item or member names itself once its file is in
  // (ItemModal, MemberCard and the phone's pages); a term other than the root by its years.
  const named = route.page === 'home' && !missing && !!(route.item || route.member);
  useEffect(() => {
    if (named) return;
    const name = trust ? TRUST_TITLES[trust] : support ? ROUTES.titles.support : report ? ROUTES.titles.report
      : missing ? ROUTES.titles.missing : termLabel && term ? term.label : '';
    pageTitle(name);
  }, [trust, support, report, missing, named, termLabel, term?.label]);

  // ---- search and the item filter -------------------------------------------------
  // A phone searches in its Find sheet, which lists what it finds and never narrows the grid.
  const query = phone ? '' : q.trim().toLowerCase();
  const { columns, rowFilter, narrowed, noMatch, delegates, shownPeople } = useMemo(() => {
    if (!current) return { columns: [], rowFilter: () => true, narrowed: false, noMatch: false, delegates: [], shownPeople: 0 };
    let cols = current.columns.map((_, i) => i).filter((i) => filter === 'all' || current.columns[i].group === filter);
    let itemHit = false;
    if (query) {
      const hit = cols.filter((i) => itemText(current.columns[i]).includes(query));
      if (hit.length) { cols = hit; itemHit = true; }
    }
    const matches = (r: Row) => personText(r).includes(query);
    const anyPerson = !!query && current.rows.some(matches);
    const rowFilter = query && anyPerson ? matches : () => true;
    // A delegate is a member this term with no row, so "no member by that name" would be untrue.
    const delegates = query ? current.delegates.filter((d) => `${d.full} ${d.seat}`.toLowerCase().includes(query)) : [];
    return {
      columns: cols, rowFilter, narrowed: anyPerson, noMatch: !!query && !anyPerson && !itemHit, delegates,
      shownPeople: new Set(current.rows.filter(rowFilter).map((r) => r.id)).size,   // people, not rows
    };
  }, [current, filter, query]);

  // Nothing here for the search: what the other terms hold for it (search.ts), each offered with its term, where
  // the same search is kept.
  const elsewhere = useElsewhere(meta, current?.congress ?? null, noMatch ? query : '',
    (g) => ({
      people: [...new Map(g.rows.filter((r) => personText(r).includes(query)).map((r) => [r.id, r.full])).values()],
      items: g.columns.filter((c) => itemText(c).includes(query)).map((c) => `${c.number}${c.title ? ', ' + c.title : ''}`),
    }),
    (x) => x.people.length + x.items.length > 0);

  const toggleLeft = useCallback((band: Band) => setOpenLeft((s) => ({ ...s, [band]: !s[band] })), []);

  // The phone's grid shows PAGE items at a time (PhoneGrid.tsx), one of them focused. Both are kept
  // here, not in the address, so Back from an item or a card returns to them; a shared link opens the
  // first page. A new term, filter or search starts again at the first; a new page focuses its first
  // item, and a month picked in Jump to a month focuses that month's newest.
  const [gridPos, setGridPos] = useState({ page: 0, i: 0 });
  useEffect(() => setGridPos({ page: 0, i: 0 }), [term?.congress, filter, query]);
  const setGridPage = useCallback((p: number) => setGridPos({ page: p, i: 0 }), []);
  const setGridFocus = useCallback((i: number) => setGridPos((s) => ({ ...s, i })), []);
  const gridPages = Math.max(1, Math.ceil(columns.length / PAGE));
  const pageNow = Math.min(gridPos.page, gridPages - 1);
  const focusNow = gridPos.page === pageNow ? gridPos.i : 0;
  const jumpToMonth = (ym: string) => {
    const i = current ? columns.findIndex((ci) => current.columns[ci].date.startsWith(ym)) : -1;
    if (i >= 0) setGridPos({ page: Math.floor(i / PAGE), i: i % PAGE });
    closeSheet();
  };
  const openFind = () => { primeKeyboard(); openSheet('find'); };
  // A name searched for that belongs to someone who left office this term sits in a "Left office
  // this term" group, which starts closed: open it, so what was searched for is on screen. Until
  // 24 Sep 2026 "McCarthy" on 2023-2024 showed "House 0 of 429" above a closed group of one.
  useEffect(() => {
    if (!current || !narrowed) return;
    const bands = [...new Set(current.rows.filter((r) => r.left && rowFilter(r)).map((r) => r.band))];
    if (bands.length) setOpenLeft((s) => (bands.every((b) => s[b]) ? s : { ...s, ...Object.fromEntries(bands.map((b) => [b, true])) }));
  }, [current, narrowed, rowFilter]);

  // ---- "Who represents me?" --------------------------------------------------------
  const [district, setDistrict] = useState<District | null>(loadDistrict);
  const [address, setAddress] = useState('');
  const [look, setLook] = useState<Look>({ k: 'idle' });
  // `via` says whether the visitor typed an address or shared a position, so every sentence
  // about what was (not) kept names the right thing.
  const choose = (c: Choice, via: Via = 'address') => {
    if (c.senators) { setDistrict(keep(c)); setLook({ k: 'found', matched: c.matched }); }
    else setLook({ k: 'nosenators', place: c.state, via });
  };
  const busy = look.k === 'finding' || look.k === 'locating';
  const find = async () => {
    if (address.trim().length < 3 || busy) return;
    setLook({ k: 'finding' });
    const r = await lookUp(address.trim());
    if (r.status === 'match') choose(r);
    else if (r.status === 'choose') {
      setLook({ k: 'choose', prompt: 'That address matched more than one place. Which is yours?', choices: r.choices, via: 'address' });
    }
    else if (r.status === 'none') setLook({ k: 'none' });
    else setLook({ k: 'failed', why: r.why, via: 'address' });
  };
  // "Use my location": found in this browser from the whole-country map (district.ts, locate.ts).
  const locateMe = async () => {
    if (!meta || busy) return;
    setLook({ k: 'locating' });
    const r = await fromPosition(meta.terms, loadDistrictMap);
    const near = 'accuracy' in r ? accuracyWords(r.accuracy) : '';
    if (r.status === 'match') choose({ ...r.choices[0], matched: `from your position, to within ${near}` }, 'position');
    else if (r.status === 'choose') {
      setLook({
        k: 'choose',
        prompt: `Your position (to within ${near}) is near a district line, so the map does not guess. Which is yours?`,
        choices: r.choices.map((c) => ({ ...c, matched: `${c.matched}, which you chose` })),
        labels: r.choices.map((c) => c.matched),
        via: 'position',
      });
    } else if (r.status === 'none') setLook({ k: 'outside' });
    else if (r.status === 'water') setLook({ k: 'water', place: r.state });
    else setLook({ k: 'failed', why: r.why, via: 'position' });
  };
  const forgetIt = () => { forget(); setDistrict(null); setLook({ k: 'idle' }); };
  // On a phone a lookup that finds a district closes its sheet and shows the grid's top, where the
  // band of the visitor's representatives now leads (Design's phone handoff, "Who represents me?").
  useEffect(() => {
    if (look.k !== 'found' || sheet !== 'lookup') return;
    closeSheet(() => {
      const g = document.querySelector('[data-sr-grid]');
      if (g) window.scrollTo({ top: g.getBoundingClientRect().top + window.scrollY });
    });
  }, [look]);
  // The band for the term on screen, from THAT Congress's district lines (district.ts).
  const pinned = useMemo((): PinnedBand | null => {
    if (!current || !district || !district.senators) return null;
    const seat = district.seats[String(current.congress)] ?? null;
    const rows = current.rows.map((r, i) => ({ r, i }))
      .filter(({ r }) => (r.role === 'sen' && r.seat === district.state) || (!!seat && r.role === 'rep' && r.seat === seat))
      .sort((a, b) => (a.r.role === b.r.role ? 0 : a.r.role === 'sen' ? -1 : 1) || +a.r.left - +b.r.left || a.i - b.i)
      .map(({ i }) => i);
    return { rows, count: seat ?? district.state };
  }, [current, district]);
  usePageColumn(current ? columns.length : null, phone);

  // ---- the phone's item and member pages (PhonePages.tsx) -----------------------------------
  // ‹ › and a swipe step through the items shown (the item filter's; all the term's, for an item it
  // leaves out), and through everyone in the grid's order, or the visitor's three representatives when
  // the page came from their band. Each step REPLACES the history entry (router.stepTo).
  const itemId = route.page === 'home' ? route.item : null;
  const order = useMemo(() => (current ? displayOrder(current, sortBy) : []), [current, sortBy]);
  const itemList = useMemo(() => {
    if (!current || !itemId) return [];
    return columns.some((ci) => current.columns[ci].id === itemId) ? columns : current.columns.map((_, i) => i);
  }, [current, columns, itemId]);
  const reps = !!memberId && fromReps() && !!pinned;
  const peopleIds = useMemo(() => {
    if (!current) return [];
    const rows = reps && pinned ? pinned.rows : order;
    return [...new Set(rows.map((r) => current.rows[r].id))];
  }, [current, order, reps, pinned]);

  const updated = meta?.built_at ?? null;
  // From meta.json, which build_grid writes from the same counts as the grid file, so the
  // line shows before the grid arrives and on pages that load no grid at all.
  const headCount = term
    ? `${ordinal(term.congress)} Congress · ${peopleIn(term)} people · ${term.columns} recorded items`
    : '';

  if (error) return <ErrorState detail={error} />;

  // The controls. The lookup's box, its note and what it does are shared by the desktop's row and the
  // phone's sheet, so the two can never behave differently.
  const search = (
    <input id="sr-search" class="input" value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)}
           placeholder="Name, state, or bill number" aria-label="Search" />
  );
  const termSelect = (
    <select class="input sr-term-select" value={term ? String(term.congress) : ''} aria-label="Congress"
            onChange={(e) => {
              const t = meta?.terms.find((x) => String(x.congress) === (e.target as HTMLSelectElement).value);
              if (t) navigate(termPath(t));
            }}>
      {meta?.terms.map((t) => (
        <option key={t.congress} value={String(t.congress)}>{t.label} · {ordinal(t.congress)} Congress</option>
      ))}
      <option disabled>Earlier Congresses — not yet loaded</option>
    </select>
  );
  // The Census Bureau's geocoder finds a street address; a ZIP code or a city alone finds
  // nothing (measured 21 Sep 2026), and one ZIP can span several districts anyway.
  const addressBox = (
    <input class="input" value={address} placeholder="Street address, city, state" autocomplete="street-address"
           aria-label="Who represents me? Street address, city, state"
           onInput={(e) => setAddress((e.target as HTMLInputElement).value)}
           onKeyDown={(e) => { if (e.key === 'Enter') find(); }} />
  );
  const findButton = (
    <button class="btn btn-secondary" onClick={find} disabled={address.trim().length < 3 || busy}>
      {look.k === 'finding' ? 'Finding…' : 'Find'}
    </button>
  );
  const FILTERS: [Filter, string][] = [['all', 'All'], ['bills', 'Bills & resolutions'], ['budget', 'Budgets & spending'], ['noms', 'Nominations']];
  const tabs = (
    <div class="sr-tabs" role="group" aria-label="Items">
      {FILTERS.map(([f, label]) => (
        <button key={f} class={'sr-tab' + (filter === f ? ' is-active' : '')} onClick={() => setFilter(f)}>{label}</button>
      ))}
    </div>
  );
  const SORTS: [SortBy, string][] = [['tenure', 'Time in office'], ['name', 'Name']];
  const sortTabs = (
    <div class="sr-tabs" role="group" aria-label="Order people by">
      {SORTS.map(([s, label]) => (
        <button key={s} class={'sr-tab' + (sortBy === s ? ' is-active' : '')} aria-pressed={sortBy === s}
                onClick={() => setSortBy(s)}>{label}</button>
      ))}
    </div>
  );
  const countLine = current ? `${shownPeople} ${shownPeople === 1 ? 'person' : 'people'} · ${columns.length} ${columns.length === 1 ? 'item' : 'items'} shown of ${current.columns.length} in this term` : '';

  // The bottom bar (Find, ‹, where you are, ›) sits on the phone's grid; an item or a card has its own Back.
  const bar = phone && route.page === 'home' && !route.item && !route.member && !!current;
  // The phone's status line: the term and the items shown, and how many people and items that is.
  const shownItems = current ? columns.length : term?.columns ?? 0;
  const people = current ? shownPeople : term ? peopleIn(term) : 0;
  const yours = district && district.senators && term ? district.seats[String(term.congress)] ?? district.state : null;
  // The Menu marks the page on screen.
  const menuHere = route.page === 'home' ? (route.item || route.member ? null : '/')
    : ({ how: '/how-this-works', corrections: '/corrections', pays: '/who-pays', privacy: '/privacy', support: '/support', report: '/report' } as Record<string, string>)[route.page] ?? null;
  const itemAt = current && itemId ? itemList.findIndex((ci) => current.columns[ci].id === itemId) : -1;
  const itemTo = (i: number) => (current && i >= 0 && i < itemList.length ? '/item/' + current.columns[itemList[i]].id : null);
  const stepItem = (dir: 1 | -1) => { const to = itemAt >= 0 ? itemTo(itemAt + dir) : null; if (to) stepTo(to); };
  const itemCol = current && itemAt >= 0 ? current.columns[itemList[itemAt]] : null;
  const itemDay = itemCol ? outcomeDay(itemCol) : null;
  const itemBar = phone && itemCol ? (
    <Bar l1={itemCol.number} l2={`${itemAt + 1} of ${itemList.length}` + (itemDay ? ' · ' + fmtLong(itemDay) : '')}
         prev={itemTo(itemAt - 1) ? () => stepItem(-1) : null} next={itemTo(itemAt + 1) ? () => stepItem(1) : null}
         prevLabel="Newer item" nextLabel="Older item" onFind={openFind} onMonths={() => openSheet('months')} />
  ) : null;
  const personAt = memberId ? peopleIds.indexOf(memberId) : -1;
  const stepMember = (dir: 1 | -1) => { const to = personAt >= 0 ? peopleIds[personAt + dir] : null; if (to) stepTo('/member/' + to); };
  const personRow = current && memberId ? current.rows[order.find((r) => current.rows[r].id === memberId) ?? -1] : undefined;
  const personWhere = !current || !personRow ? 'Not in the grid'
    : reps ? `${personAt + 1} of ${peopleIds.length} · yours`
    : personRow.left ? 'Left office this term'
    : (() => {
      const serving = order.filter((r) => current.rows[r].band === personRow.band && !current.rows[r].left);
      const at = serving.findIndex((r) => current.rows[r].id === personRow.id) + 1;
      return `${{ executive: 'White House', senate: 'Senate', house: 'House' }[personRow.band]} · ${at} of ${serving.length}`;
    })();
  const memberBar = phone && current && memberId ? (
    <Bar l1={personRow?.last ?? current.delegates.find((d) => d.id === memberId)?.full ?? ''} l2={personWhere}
         prev={personAt > 0 ? () => stepMember(-1) : null}
         next={personAt >= 0 && personAt < peopleIds.length - 1 ? () => stepMember(1) : null}
         prevLabel="Previous person" nextLabel="Next person" onFind={openFind} onMonths={() => openSheet('months')} />
  ) : null;
  // Jump to a month: the grid goes to the page holding it, an item to its newest item, a member's page
  // to that month of their votes.
  const monthJump = (ym: string) => {
    if (memberId) { closeSheet(() => jumpToMonthOf(ym)); return; }
    if (itemId && current) {
      const to = itemTo(itemList.findIndex((ci) => current.columns[ci].date.startsWith(ym)));
      closeSheet(to ? () => stepTo(to) : undefined);
      return;
    }
    jumpToMonth(ym);
  };
  const toBand = (key: string) => closeSheet(() => jumpToBand(key));
  const menuGo = (path: string) => {
    if (path === location.pathname + location.search + location.hash) closeSheet(() => window.scrollTo(0, 0));
    else { leaveSheet(path); window.scrollTo(0, 0); }
  };

  return (
    <div class={'sr-app' + (phone ? ' is-phone' : '') + (bar ? ' has-bar' : '')}>
      <Pinned z={60} class="sr-chrome">
        {phone ? <PhoneMast onMenu={() => openSheet('menu')} /> : <>
        <Nav active={support ? 'support' : page ? null : 'home'} updated={updated} />
        <Masthead headCount={headCount} />
        </>}
        <Epigraph />
        {!page && !phone && <>
        <div class="sr-controls">
          <div class="sr-control sr-control-lookup">
            <div class="sr-label">Who represents me?</div>
            <div class="sr-control-row">
              {addressBox}
              {findButton}
              <button class="btn btn-secondary" onClick={locateMe} disabled={!meta || busy}>
                {look.k === 'locating' ? 'Locating…' : 'Use my location'}
              </button>
            </div>
            <LookupNote look={look} district={district} congress={term?.congress ?? null} onChoose={choose}
                        onForget={forgetIt} locate="Use my location" />
          </div>
          <div class="sr-control sr-control-search">
            <div class="sr-label">Search</div>
            {search}
          </div>
          <div class="sr-control">
            <div class="sr-label">Congress</div>
            {termSelect}
          </div>
          <div class="sr-control">
            <div class="sr-label">Items</div>
            {tabs}
          </div>
          <div class="sr-control">
            <div class="sr-label">Order people by</div>
            {sortTabs}
          </div>
        </div>
        <OutcomeKey />
        </>}
        {/* The phone's (Design's phone handoff, "Chrome"): Find and Who represents me? as two buttons, and
            one status line that opens the term, the item filter and the key. */}
        {!page && phone && term && (
          <div class="sr-pcontrols">
            <div class="sr-pcontrols-row">
              <button class="sr-pfield" aria-haspopup="dialog" onClick={openFind}>Name, state, or bill</button>
              <button class="sr-plook" aria-haspopup="dialog" onClick={() => openSheet('lookup')}>
                {yours ? `Yours: ${yours}` : 'Who represents me?'}
              </button>
            </div>
            <button class="sr-pstatus" aria-haspopup="dialog" onClick={() => openSheet('options')}>
              <span class="sr-pstatus-text">
                <span class="sr-pstatus-l1">
                  {term.label} · {PHONE_FILTERS.find(([f]) => f === filter)?.[1]}{sortBy === 'name' ? ' · By name' : ''}
                </span>
                <span class="sr-pstatus-l2">
                  {people} {people === 1 ? 'person' : 'people'} · {shownItems} {shownItems === 1 ? 'item' : 'items'}
                </span>
              </span>
              <span class="sr-pstatus-go">Term, items, key ›</span>
            </button>
          </div>
        )}
      </Pinned>

      {trust && <TrustDoc page={trust} />}
      {support && <SupportPage />}
      {missing && <NotFound path={path} />}
      {report && <ReportPage key={location.search} />}

      {!page && !current && <Loading />}

      {!page && current && noMatch && (
        <Pinned class="sr-nomatch">
          {delegates.length ? (
            <>
              <h3>Not in the grid: {listed(delegates.map((d) => d.full))}.</h3>
              {delegates.map((d) => (
                <p key={d.id}>
                  {d.full}, the member of the House for {NON_STATES[d.seat.split('-')[0]] ?? d.seat}, cannot vote on
                  final passage, so is not in a grid of final votes. <CardLink id={d.id}>Open the member card</CardLink>
                </p>
              ))}
            </>
          ) : (
            <>
              <h3>Nothing matches “{q.trim()}”.</h3>
              <p>Search by a member’s last name, a state, or a bill number such as H.R. 1. Nothing is hidden — this term simply has no member or item by that name.</p>
              {elsewhere === null && <p class="sr-muted">Looking in the other terms…</p>}
              {elsewhere?.map(({ term: t, found }) => {
                const names = [...found.items, ...found.people];
                return (
                  <p key={t.congress}>
                    In {t.label}: {names.slice(0, 3).join('; ')}{names.length > 3 ? `; and ${names.length - 3} more` : ''}.{' '}
                    <button class="btn btn-secondary" onClick={() => navigate(termPath(t))}>Show it in {t.label}</button>
                  </p>
                );
              })}
            </>
          )}
          <button class="btn btn-secondary" onClick={() => setQ('')}>Clear the search</button>
        </Pinned>
      )}

      {!page && current && !noMatch && (
        <>
          {phone ? (
            <PhoneGrid grid={current} columns={columns} rowFilter={rowFilter} openLeft={openLeft} pinned={pinned}
                       onToggleLeft={toggleLeft} page={pageNow} pages={gridPages} onPage={setGridPage}
                       focus={focusNow} onFocus={setGridFocus} onBand={() => openSheet('jump')} sortBy={sortBy} />
          ) : (
            <Grid grid={current} columns={columns} rowFilter={rowFilter} openLeft={openLeft} pinned={pinned}
                  onToggleLeft={toggleLeft} selectedItem={route.page === 'home' ? route.item : null} phone={phone}
                  sortBy={sortBy} />
          )}
          {!phone && (
            <Pinned class="sr-count-note">
              <span>{countLine}</span>
              <span>Scroll sideways for the rest of the term. Hover a cell for the full record.</span>
            </Pinned>
          )}
          <DelegatesNote grid={current} />
        </>
      )}

      <Footer updated={updated} page={route.page} phone={phone} />
      {route.page === 'home' && route.item && term && (phone
        ? <ItemPage id={route.item} grid={current} pinned={pinned} label={backLabel()} bar={itemBar} updated={updated}
                    onClose={close} onStep={stepItem} onBand={() => openSheet('jump')} sortBy={sortBy} />
        : <ItemModal id={route.item} onClose={close} />
      )}
      {route.page === 'home' && route.member && current && (phone
        ? <MemberPage id={route.member} grid={current} pinned={pinned} reps={reps} label={backLabel()} bar={memberBar}
                      updated={updated} onClose={close} onStep={stepMember} onTab={(id) => stepTo('/member/' + id)} />
        : <MemberCard id={route.member} grid={current} onClose={close} />
      )}
      {bar && current && (
        <PhoneBar grid={current} columns={columns} page={pageNow} pages={gridPages} onPage={setGridPage}
                  onFind={openFind} onMonths={() => openSheet('months')} />
      )}
      {/* The phone's sheets, last, so each is a child of this root beside what it makes inert. */}
      {phone && sheet === 'find' && current && (
        <FindSheet grid={current} meta={meta} onClose={() => closeSheet()}
                   openPerson={(congress, id) => { lastTerm.current = congress; leaveSheet('/member/' + id); }} />
      )}
      {phone && sheet === 'lookup' && (
        <Sheet title="Who represents me?" onClose={() => closeSheet()}>
          <div class="sr-plookup">
            {addressBox}
            <div class="sr-plookup-btns">
              <button class="sr-plookup-find" onClick={find} disabled={address.trim().length < 3 || busy}>
                {look.k === 'finding' ? 'Finding…' : 'Find'}
              </button>
              <button class="sr-plookup-loc" onClick={locateMe} disabled={!meta || busy}>
                {look.k === 'locating' ? 'Locating…' : 'Use my location'}
              </button>
            </div>
            <LookupNote look={look} district={district} congress={term?.congress ?? null} onChoose={choose}
                        onForget={forgetIt} locate="Use my location" />
          </div>
        </Sheet>
      )}
      {phone && sheet === 'options' && meta && term && (
        <OptionsSheet meta={meta} term={term} filter={filter} legend={current?.legend ?? null}
                      bands={current ? bandList(current, pinned) : []} onJump={toBand}
                      onTerm={(t) => navigateUnderSheet(termPath(t))} onFilter={setFilter} onClose={() => closeSheet()}
                      sortBy={sortBy} onSort={setSortBy} />
      )}
      {phone && sheet === 'months' && current && (
        <MonthsSheet months={memberId ? memberMonths(current, memberId).map((g) => ({ ym: g.ym, n: g.votes.length }))
                             : termMonths(current, itemId ? itemList : columns)}
                     onJump={monthJump} onClose={() => closeSheet()} />
      )}
      {phone && sheet === 'menu' && (
        <MenuSheet current={menuHere} onGo={menuGo} onStates={() => openSheet('states')} onClose={() => closeSheet()} />
      )}
      {phone && sheet === 'states' && <StatesLayer />}
      {phone && sheet === 'jump' && current && (
        <JumpSheet bands={bandList(current, pinned)} onJump={toBand} onClose={() => closeSheet()} />
      )}
      <div id="sr-tip" class="sr-tip" role="tooltip" />
      {/* A phone scrolls sideways with a swipe; the scrubber is for a mouse. */}
      {!phone && <Scrubber />}
    </div>
  );
}

type Via = 'address' | 'position';
type Look =
  | { k: 'idle' } | { k: 'finding' } | { k: 'locating' } | { k: 'none' } | { k: 'outside' }
  | { k: 'failed'; why: string; via: Via } | { k: 'found'; matched: string }
  | { k: 'choose'; prompt: string; choices: Choice[]; labels?: string[]; via: Via }
  | { k: 'nosenators'; place: string; via: Via } | { k: 'water'; place: string };
const NOT_KEPT: Record<Via, string> = {
  address: 'That address was not stored.',
  position: 'Your position was not sent or stored.',
};

const opening = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const listed = (names: string[]) => names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

function CardLink({ id, children }: { id: string; children: ComponentChildren }) {
  return (
    <a href={'/member/' + id} onClick={(e) => {
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault(); navigate('/member/' + id);
    }}>{children}</a>
  );
}

/** The States list, opened from a phone's Menu: the whole screen, and a sheet, so Back closes it. */
function StatesLayer() {
  const ref = useRef<HTMLDivElement>(null);
  useLayer(ref, () => closeSheet(), '.sr-states-close');
  return (
    <div ref={ref} class="sr-layer">
      <StatesPanel onClose={() => closeSheet()} onPick={() => goStates(true)} />
    </div>
  );
}

/** The brief: the House's non-voting members are "left out of the grid, named in a footnote".
 *  Each name opens the member's card, which says the same. */
function DelegatesNote({ grid }: { grid: GridDoc }) {
  const ds = grid.delegates;
  if (!ds.length) return null;
  return (
    <Pinned class="sr-footnote">
      Not in the grid: the members of the House for Washington, DC and the territories, who cannot vote on final
      passage.{' '}
      {ds.map((d, i) => (
        <Fragment key={d.id}>
          {i === 0 ? '' : i === ds.length - 1 ? ' and ' : ', '}
          <CardLink id={d.id}>{d.full}</CardLink> ({NON_STATES[d.seat.split('-')[0]] ?? d.seat})
        </Fragment>
      ))}.
    </Pinned>
  );
}

/** The line under the address box: what the lookup did, and what is kept. Every sentence is a
 *  claim the Privacy page also makes, so the two say the same thing. A district kept from an
 *  earlier lookup is named whatever the latest lookup did, because its band is still on screen
 *  and a note saying "nothing was pinned" beside it would contradict the page. */
function LookupNote({ look, district, congress, onChoose, onForget, locate }: {
  look: Look; district: District | null; congress: number | null;
  onChoose: (c: Choice, via: Via) => void; onForget: () => void; locate: string;
}) {
  let kept = null;
  if (district && district.senators) {
    const key = congress == null ? null : String(congress);
    const seat = key ? district.seats[key] ?? null : null;
    // A district kept from a lookup made before this term was added has no seat for it at all: that is
    // not "no seat was found", so say what happened and how to get one.
    const why = key ? district.missing?.[key] ?? (key in district.seats ? undefined
      : 'your district was found before this term was added to the site; look it up again to pin this term’s representative')
      : undefined;
    kept = (
      <>
        {look.k === 'found' ? 'Pinned above the grid' : 'Pinned above the grid from your earlier lookup'}:{' '}
        {district.state}’s senators{seat ? <> and {seat}</> : null}.
        {!seat && key && <> No representative is pinned for this term: {why ?? 'no House seat was found on this Congress’s district lines'}.</>}
        {' '}Your district is kept only in this browser.{' '}
        <button class="sr-link" onClick={onForget}>Forget it</button>
      </>
    );
  }
  let latest = null;
  if (look.k === 'finding') latest = <>Looking up… sent through this site’s server to the Census Bureau, and not stored. </>;
  else if (look.k === 'locating') {
    latest = <>Asking your browser for your position, then finding your district from a map of every district, inside this browser. Your position is not sent anywhere. </>;
  } else if (look.k === 'outside') {
    latest = <>Your position is not in any U.S. congressional district on this Congress’s map. Nothing was pinned, and your position was not sent or stored. </>;
  } else if (look.k === 'water') {
    latest = <>Your position is in water that the Census Bureau assigns to no congressional district, in {look.place}. Nothing was pinned, and your position was not sent or stored; the address box finds a district for any street address. </>;
  }
  else if (look.k === 'none') {
    latest = <>No match. The Census Bureau’s geocoder needs a street address with the city and state; a ZIP code or a city alone finds nothing. That address was not stored. </>;
  } else if (look.k === 'failed') latest = <>The lookup did not work: {look.why}. {NOT_KEPT[look.via]} </>;
  else if (look.k === 'nosenators') {
    latest = (
      <>
        {opening(NON_STATES[look.place] ?? look.place)} elects a member of the House who cannot vote on final passage, so is
        not in the grid, and has no senators, so there is no one to pin. {NOT_KEPT[look.via]}{' '}
      </>
    );
  } else if (look.k === 'choose') {
    latest = (
      <>
        {look.prompt}{' '}
        {look.choices.map((c, i) => (
          <button key={c.matched} class="sr-link sr-choice" onClick={() => onChoose(c, look.via)}>{look.labels?.[i] ?? c.matched}</button>
        ))}{' '}
      </>
    );
  } else if (look.k === 'found') latest = <>Found {look.matched}. </>;
  if (!latest && !kept) {
    return (
      <div class="sr-note">
        Nothing you type is stored, and with {locate} your position never leaves this device. Only your district is
        kept, in this browser.
      </div>
    );
  }
  return <div class="sr-note" role="status">{latest}{kept}</div>;
}

// The page column: masthead, controls, footer and grid share one left edge, and recentre
// together when the window is wider than the table (README, "Page column centring").
function usePageColumn(columnCount: number | null, phone: boolean) {
  const lastGridWidth = useRef(1369);
  useEffect(() => {
    const sync = () => {
      const root = document.documentElement, vw = root.clientWidth;
      // Never write a zero: an explicit 0px kills the var(…, 100vw) fallback and collapses
      // every pinned wrapper if layout is not resolved yet.
      if (vw <= 0) { requestAnimationFrame(sync); return; }
      root.style.setProperty('--sr-vw', vw + 'px');
      const g = document.querySelector('[data-sr-grid]') as HTMLElement | null;
      if (g) lastGridWidth.current = g.scrollWidth;
      // A phone keeps the design's 16px margin; wider windows centre the column on the grid.
      root.style.setProperty('--sr-pad', (phone ? 16 : Math.max(24, (vw - Math.min(lastGridWidth.current, 1512)) / 2 + 24)) + 'px');
      window.dispatchEvent(new Event('sr:layout'));
    };
    sync();
    requestAnimationFrame(sync);
    const ro = window.ResizeObserver ? new ResizeObserver(sync) : null;
    ro?.observe(document.documentElement);
    return () => ro?.disconnect();
  }, [columnCount, phone]);
}

// A 7,000px-tall page puts the native horizontal scrollbar far below the fold, so a
// scrubber is fixed to the bottom of the viewport (README, "Bottom scrubber").
function Scrubber() {
  const track = useRef<HTMLDivElement>(null);
  const thumb = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sync = () => {
      const t = thumb.current, tr = track.current;
      if (!t || !tr) return;
      const root = document.documentElement, vw = root.clientWidth, dw = root.scrollWidth;
      if (dw <= vw + 4) { tr.style.visibility = 'hidden'; return; }
      tr.style.visibility = 'visible';
      const w = Math.max(48, Math.round((vw / dw) * (vw - 8)));
      t.style.width = w + 'px';
      t.style.left = 4 + (window.scrollX / (dw - vw)) * (vw - 8 - w) + 'px';
    };
    sync();
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    window.addEventListener('sr:layout', sync);
    const ro = window.ResizeObserver ? new ResizeObserver(sync) : null;
    ro?.observe(document.body);
    return () => {
      window.removeEventListener('scroll', sync); window.removeEventListener('resize', sync);
      window.removeEventListener('sr:layout', sync); ro?.disconnect();
    };
  }, []);
  const scrubTo = (clientX: number) => {
    const dw = document.documentElement.scrollWidth, vw = window.innerWidth;
    const f = Math.min(1, Math.max(0, (clientX - 24) / (vw - 48)));
    window.scrollTo({ left: f * (dw - vw) });
  };
  const down = (e: MouseEvent) => {
    e.preventDefault();
    scrubTo(e.clientX);
    const move = (ev: MouseEvent) => scrubTo(ev.clientX);
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };
  return (
    <div ref={track} class="sr-scrub" onMouseDown={down} aria-hidden="true">
      <div ref={thumb} class="sr-scrub-thumb" />
    </div>
  );
}

function Loading() {
  return (
    <Pinned class="sr-loading">
      <div class="sr-label">Loading the record · one file, one request</div>
      {Array.from({ length: 18 }, (_, i) => (
        <div key={i} class="sr-skel" style={{ width: 100 - (i % 5) * 7 + '%', animationDelay: i * 0.06 + 's' }} />
      ))}
    </Pinned>
  );
}

function ErrorState({ detail }: { detail: string }) {
  return (
    <div class="sr-error">
      <div class="sr-label">Votes on Record</div>
      <h2>The record could not be loaded.</h2>
      <p>The file that failed: <code>{detail}</code>. Nothing about you was recorded in the failure.</p>
      <button class="btn btn-secondary" onClick={() => location.reload()}>Try again</button>
    </div>
  );
}
